/**
 * Homepage In-Feed Ad Helpers
 *
 * Now uses the consolidated adInsertion system for shared logic.
 *
 * @module lib/homepageInfeedAds
 */

const {
  createInsertNewAdFunction,
  ensureAdState,
  hasAdAfter,
  insertAdAfterElement,
  syncPlacedAdCountFromDom,
  getSlotPrefix
} = require('./adInsertion');
const { ensureAdSlotMinHeight } = require('./adSizeUtils');
const { clearContentAdSticky } = require('./contentAdSticky');
const { logWarn } = require('./logger');
const { injectStyles } = require('./utils/styleInjector');

const HOMEPAGE_INFEED_AD_STYLES_ID = 'milla-homepage-infeed-ad-styles';
const HOMEPAGE_INFEED_AD_STYLES = `
.milla-ads-infeed {
  padding-bottom: 32px;
}
`;

/**
 * Default configuration for homepage in-feed ads
 */
const HOMEPAGE_INFEED_DEFAULTS = {
  adInterval: 4,
  selectors: ['.container.feed .posts-wrapper'],
  widgetClass: 'widget',
  slotPrefix: 'dt-content-ad',
  minHeight: 0
};

function ensureHomepageInfeedAdStyles() {
  injectStyles(HOMEPAGE_INFEED_AD_STYLES_ID, HOMEPAGE_INFEED_AD_STYLES);
}

/**
 * Check if element is a posts wrapper
 */
function isPostsWrapper(el) {
  if (!el || !el.classList) return false;
  return el.classList.contains('posts-wrapper');
}

/**
 * Check if element is a widget
 */
function isWidget(el) {
  if (!el || !el.classList) return false;
  return el.classList.contains('widget');
}

function resetBoxStyles(node) {
  if (!node || !node.style) return;
  node.style.display = '';
  node.style.height = '';
  node.style.minHeight = '';
  node.style.width = '';
  node.style.minWidth = '';
  node.style.margin = '';
  node.style.padding = '';
  node.style.overflow = '';
  if (typeof node.removeAttribute === 'function') {
    node.removeAttribute('aria-hidden');
  }
}

function zeroBoxStyles(node, hide) {
  if (!node || !node.style) return;
  if (hide) {
    node.style.display = 'none';
  }
  node.style.height = '0px';
  node.style.minHeight = '0px';
  node.style.width = hide ? '0px' : '';
  node.style.minWidth = '0px';
  node.style.margin = '0';
  node.style.padding = '0';
  node.style.overflow = 'hidden';
  if (hide && typeof node.setAttribute === 'function') {
    node.setAttribute('aria-hidden', 'true');
  }
}

function getAdWrapper(slot) {
  if (!slot || typeof slot.closest !== 'function') return null;
  return slot.closest('.ad-wrapper');
}

function getAdFlex(slot) {
  if (!slot || typeof slot.closest !== 'function') return null;
  return slot.closest('.ad-flex');
}

function isHomepageInfeedSlotId(slotId, slotPrefix) {
  if (!slotId || !slotPrefix) return false;
  return typeof slotId === 'string' && slotId.indexOf(`${slotPrefix}-`) === 0;
}

function prepareHomepageInfeedAdSlot(slot) {
  if (!slot) return;
  clearContentAdSticky(slot);
  if (slot.dataset) {
    slot.dataset.millaAdState = 'pending';
  }
  const wrapper = getAdWrapper(slot);
  if (wrapper && wrapper.dataset) {
    wrapper.dataset.millaAdState = 'pending';
  }
}

function collapseHomepageInfeedAdSlot(slot) {
  if (!slot) return false;
  if (typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) {
    return false;
  }
  clearContentAdSticky(slot);
  if (slot.dataset) {
    slot.dataset.millaAdState = 'collapsed';
  }
  const flex = getAdFlex(slot);
  const wrapper = getAdWrapper(slot);
  if (wrapper && wrapper.dataset) {
    wrapper.dataset.millaAdState = 'collapsed';
  }
  zeroBoxStyles(slot, true);
  zeroBoxStyles(flex, true);
  zeroBoxStyles(wrapper, true);
  return true;
}

function restoreHomepageInfeedAdSlot(slot) {
  if (!slot) return false;
  clearContentAdSticky(slot);
  resetBoxStyles(slot);
  const flex = getAdFlex(slot);
  const wrapper = getAdWrapper(slot);
  resetBoxStyles(flex);
  resetBoxStyles(wrapper);
  if (slot.dataset) {
    slot.dataset.millaAdState = 'filled';
  }
  if (wrapper && wrapper.dataset) {
    wrapper.dataset.millaAdState = 'filled';
  }
  return true;
}

function createHomepageInfeedAdInserter(slotPrefix, minHeight = 0) {
  ensureHomepageInfeedAdStyles();
  return createInsertNewAdFunction(slotPrefix, minHeight, {
    wrapperClass: 'milla-ads-infeed',
    prepareSlot: prepareHomepageInfeedAdSlot
  });
}

function getHomepageInfeedContainers(selectors) {
  if (typeof document === 'undefined') return [];
  const containerSelectors = Array.isArray(selectors) ? selectors : [selectors];
  const containers = new Set();

  containerSelectors.forEach((selector) => {
    if (!selector) return;
    document.querySelectorAll(selector).forEach((container) => {
      containers.add(container);
    });
  });

  return Array.from(containers);
}

function getHomepageInfeedItems(containers, widgetClass) {
  return containers.reduce((items, container) => {
    if (!container || typeof container.querySelectorAll !== 'function') return items;
    if (container.classList) {
      if (container.classList.contains('slick-initialized')) return items;
      if (container.classList.contains('slick-slider')) return items;
    }
    container.setAttribute('data-milla-infeed-scanned', 'true');
    return items.concat(
      Array.from(container.querySelectorAll(`.${widgetClass}`)).filter((el) => (
        el &&
        el.nodeType === 1 &&
        isWidget(el) &&
        !el.querySelector('.content-ad')
      ))
    );
  }, []);
}

function insertHomepageInfeedAdAfterItem(item, options) {
  const {
    slotPrefix,
    markerClass,
    dataAttr,
    minHeight
  } = options;

  if (hasAdAfter(item, dataAttr, markerClass)) {
    item.setAttribute(dataAttr, 'true');
    return false;
  }

  try {
    ensureAdState();
    if (typeof window.insertNewAd === 'function') {
      const beforeCount = window.adData && window.adData.placedAdCount;
      window.insertNewAd(item);
      const afterCount = window.adData && window.adData.placedAdCount;
      if (afterCount && beforeCount && afterCount === beforeCount) {
        return false;
      }
    } else {
      insertAdAfterElement(item, {
        slotPrefix,
        markerClass,
        minHeight
      });
    }

    const inserted = item.nextElementSibling;
    if (inserted && inserted.classList) {
      inserted.classList.add(markerClass);
    }
    if (inserted) {
      const slot = inserted.querySelector ? inserted.querySelector('.content-ad') : null;
      if (minHeight > 0) {
        ensureAdSlotMinHeight(slot, minHeight);
      }
      prepareHomepageInfeedAdSlot(slot);
    }
    item.setAttribute(dataAttr, 'true');
    return true;
  } catch (err) {
    logWarn('[infeed] Failed to insert ad', err);
    return false;
  }
}

/**
 * Insert ads into a single homepage posts wrapper
 * @param {HTMLElement} postsWrapper - The posts wrapper element
 * @param {object} options - Configuration options
 */
function insertHomepageInfeedAds(postsWrapper, options = {}) {
  if (!postsWrapper) return 0;
  return scanHomepageInfeedAds({
    ...options,
    containers: [postsWrapper]
  });
}

/**
 * Scan all homepage in-feed containers and insert ads
 * @param {object} options - Configuration options
 */
function scanHomepageInfeedAds(options = {}) {
  if (typeof document === 'undefined') return 0;
  if (typeof window.insertNewAd !== 'function' && !options.forceInsert) return 0;

  ensureHomepageInfeedAdStyles();
  const adInterval = options.adInterval || HOMEPAGE_INFEED_DEFAULTS.adInterval;
  const widgetClass = options.widgetClass || HOMEPAGE_INFEED_DEFAULTS.widgetClass;
  const slotPrefix = options.slotPrefix || HOMEPAGE_INFEED_DEFAULTS.slotPrefix;
  const minHeight = options.minHeight || HOMEPAGE_INFEED_DEFAULTS.minHeight;
  const dataAttr = options.dataAttr || 'data-milla-ad-after';
  const markerClass = options.markerClass || 'milla-ads-infeed';
  const containers = options.containers || getHomepageInfeedContainers(
    options.selectors || HOMEPAGE_INFEED_DEFAULTS.selectors
  );
  const items = getHomepageInfeedItems(containers, widgetClass);
  let insertedCount = 0;

  items.forEach((item, index) => {
    if ((index + 1) % adInterval !== 0) return;
    if (!item || typeof item.setAttribute !== 'function') return;
    if (item.getAttribute(dataAttr) === 'true') return;
    if (insertHomepageInfeedAdAfterItem(item, {
      slotPrefix,
      markerClass,
      dataAttr,
      minHeight
    })) {
      insertedCount += 1;
    }
  });

  return insertedCount;
}

/**
 * Set up a MutationObserver for homepage in-feed ads
 * @param {object} options - Configuration options
 * @returns {MutationObserver|null}
 */
function setupHomepageInfeedAdsObserver(options = {}) {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return null;
  }
  const slotPrefix = options.slotPrefix || HOMEPAGE_INFEED_DEFAULTS.slotPrefix;
  if (typeof window !== 'undefined') {
    const guardKey = `__millaHomepageInfeedAdsObserver_${slotPrefix}`;
    if (window[guardKey]) return window[guardKey];
  }

  ensureHomepageInfeedAdStyles();
  const root = document.documentElement || document.body;
  if (!root) return null;

  const scan = () => scanHomepageInfeedAds(options);
  let queued = false;
  const queueScan = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      scan();
    }, 100);
  };

  const observer = new MutationObserver((mutations) => {
    for (let i = 0; i < mutations.length; i += 1) {
      const mutation = mutations[i];
      if (!mutation.addedNodes || mutation.addedNodes.length === 0) continue;
      for (let j = 0; j < mutation.addedNodes.length; j += 1) {
        const node = mutation.addedNodes[j];
        if (!node || node.nodeType !== 1) continue;
        if (
          (node.classList && (node.classList.contains('posts-wrapper') || node.classList.contains('widget'))) ||
          (node.querySelector && (node.querySelector('.posts-wrapper') || node.querySelector('.widget')))
        ) {
          queueScan();
          return;
        }
      }
    }
  });

  observer.observe(root, { childList: true, subtree: true });
  setTimeout(scan, 100);
  setTimeout(scan, 1000);
  if (typeof window !== 'undefined') {
    const guardKey = `__millaHomepageInfeedAdsObserver_${slotPrefix}`;
    window[guardKey] = observer;
  }
  return observer;
}

/**
 * Get homepage in-feed config for a specific brand/device
 * @param {string} brand - Brand name ('narcity' or 'mtlblog')
 * @param {string} device - Device type ('desktop' or 'mobile')
 * @returns {object} Configuration for homepage in-feed ads
 */
function getHomepageInfeedConfig(brand, device = 'desktop') {
  return {
    ...HOMEPAGE_INFEED_DEFAULTS,
    slotPrefix: getSlotPrefix(brand, device, HOMEPAGE_INFEED_DEFAULTS.slotPrefix)
  };
}

module.exports = {
  HOMEPAGE_INFEED_DEFAULTS,
  isPostsWrapper,
  isWidget,
  isHomepageInfeedSlotId,
  prepareHomepageInfeedAdSlot,
  collapseHomepageInfeedAdSlot,
  restoreHomepageInfeedAdSlot,
  insertHomepageInfeedAds,
  scanHomepageInfeedAds,
  setupHomepageInfeedAdsObserver,
  createHomepageInfeedAdInserter,
  createInsertNewAdFunction,
  syncPlacedAdCountFromDom,
  getHomepageInfeedConfig
};
